import type { KeyboardEventHandler, ReactNode, RefObject } from 'react'
import clsx from 'clsx'
import { LockIcon, SearchIcon } from '../icons/icons'
import { Tooltip } from './Tooltip'
import { isKeyboardClick } from '../hooks/useClickIntent'

export function ValueSelectorDisabledIndicator({
  content,
  className,
}: {
  content: ReactNode
  className: string
}) {
  return (
    <span className={className}>
      <Tooltip
        content={content}
        placement="right"
        sideOffsetPx={16}
        contentClassName="w-auto whitespace-nowrap"
      >
        <LockIcon size={12} aria-hidden="true" />
      </Tooltip>
    </span>
  )
}

/**
 * An option's lower-emphasis secondary label, right-aligned after its name.
 * Beside a description it may wrap onto two lines; otherwise it truncates.
 */
export function ValueSelectorSecondaryLabel({
  option,
  wraps,
  className,
}: {
  option: { secondaryLabel?: string; secondaryLeading?: ReactNode }
  wraps: boolean
  className: string
}) {
  return (
    <span
      className={clsx(
        'ml-auto flex min-w-0 shrink-0 items-center gap-1 truncate text-xs font-normal',
        wraps && 'max-w-[45%] text-right',
        className
      )}
    >
      {option.secondaryLeading ? (
        <span className="flex shrink-0 items-center">{option.secondaryLeading}</span>
      ) : null}
      <span className={wraps ? 'whitespace-normal leading-4' : 'truncate'}>
        {option.secondaryLabel}
      </span>
    </span>
  )
}

export function ValueSelectorSearchInput({
  inputRef,
  value,
  onChange,
  onKeyDown,
  ariaLabel,
  fieldLikeVariant,
  cellVariant,
  className,
}: {
  inputRef: RefObject<HTMLInputElement>
  value: string
  onChange: (value: string) => void
  onKeyDown: KeyboardEventHandler<HTMLInputElement>
  ariaLabel?: string
  fieldLikeVariant: boolean
  cellVariant: boolean
  className: string
}) {
  const input = (
    <input
      ref={inputRef}
      type="text"
      aria-label={fieldLikeVariant ? `Search ${ariaLabel ?? 'values'}` : undefined}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={onKeyDown}
      placeholder="Search..."
      className={
        fieldLikeVariant
          ? clsx(
              'h-10 w-full border-0 outline-none transition-colors focus:outline-none',
              cellVariant
                ? 'table-grid-inline-popover-search-input min-w-0 bg-transparent'
                : 'surface-default pl-8 pr-4 text-sm text-default placeholder:font-light placeholder:italic placeholder:text-muted'
            )
          : className
      }
    />
  )

  if (!fieldLikeVariant) return input

  return (
    <div
      className={clsx(
        'relative flex-none',
        cellVariant
          ? 'table-grid-inline-popover-search-row table-grid-inline-popover-search-layout'
          : 'border-b border-muted'
      )}
    >
      <SearchIcon
        size={14}
        className={clsx(
          'pointer-events-none',
          cellVariant
            ? 'table-grid-inline-popover-search-icon'
            : 'absolute left-[9px] top-1/2 -translate-y-1/2 text-faint'
        )}
        aria-hidden="true"
      />
      {input}
    </div>
  )
}

export function ValueSelectorOptionButton({
  index,
  optionId,
  expanded,
  selected,
  ariaLabel,
  disabled,
  disabledReason,
  onSelect,
  onHighlight,
  onHover = onHighlight,
  className,
  children,
}: {
  index: number
  /** Marks the row, so a side list can sit level with it. */
  optionId?: string
  /** A grouping option: whether its side list is open. */
  expanded?: boolean
  selected: boolean
  ariaLabel?: string
  /** Stays focusable and announced, but selecting it does nothing. */
  disabled?: boolean
  disabledReason?: string
  onSelect: () => void
  onHighlight: (index: number) => void
  /** Pointer highlight; defaults to `onHighlight`. */
  onHover?: (index: number) => void
  className: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      data-value-selector-option="true"
      data-value-selector-option-id={optionId}
      aria-haspopup={expanded === undefined ? undefined : 'menu'}
      aria-expanded={expanded}
      aria-label={ariaLabel}
      aria-current={selected || undefined}
      aria-disabled={disabled || undefined}
      aria-description={disabled ? disabledReason : undefined}
      onFocus={() => onHighlight(index)}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return
        event.preventDefault()
        onSelect()
      }}
      onClick={(event) => {
        if (isKeyboardClick(event)) onSelect()
      }}
      onMouseEnter={() => onHover(index)}
      className={className}
    >
      {children}
    </button>
  )
}
